import { Popup } from "devextreme-react";
import { Button } from "devextreme-react/button";
import { CheckBox } from "devextreme-react/check-box";
import { DateBox } from "devextreme-react/date-box";
import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isIndefiniteDate, SharedEmployee2 } from "../types";

type Props = {
  visible: boolean;
  isMobile: boolean;
  target:
    | (SharedEmployee2 & {
        releaseStartDate?: string | null;
        releaseEndDate?: string | null;
      })
    | null;
  initialStart?: string | Date | null;
  initialEnd?: string | Date | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    startDate: Date;
    endDate: Date;
    indefinite: boolean;
  }) => Promise<void>;
};

export const EditReleaseRangePopup2 = memo(function EditReleaseRangePopup2({
  visible,
  isMobile,
  target,
  initialStart,
  initialEnd,
  submitting,
  onClose,
  onSubmit,
}: Props) {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [indefinite, setIndefinite] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      const s = initialStart ? new Date(initialStart) : new Date();
      const indef = initialEnd ? isIndefiniteDate(initialEnd) : false;
      const e =
        initialEnd && !indef
          ? new Date(initialEnd)
          : new Date(s.getTime() + 7 * 86400000);
      setStartDate(s);
      setEndDate(e);
      setIndefinite(indef);
      setError(null);
    }
  }, [visible, initialStart, initialEnd]);

  const handleSubmit = async () => {
    if (!indefinite && endDate < startDate) {
      setError(t("transferOfEmployees2.endBeforeStart"));
      return;
    }
    setError(null);
    await onSubmit({ startDate, endDate, indefinite });
  };

  return (
    <Popup
      visible={visible}
      onHiding={onClose}
      fullScreen={isMobile}
      maxWidth={isMobile ? undefined : 480}
      height={isMobile ? "100%" : "auto"}
      maxHeight={isMobile ? undefined : "85vh"}
      title={t("transferOfEmployees2.editRangeTitle")}
      showCloseButton
      dragEnabled={!isMobile}
    >
      <div className="transfer2-form">
        <p className="transfer2-form-hint">
          {t("transferOfEmployees2.editRangeHint")}
        </p>
        {target && (
          <div className="transfer2-form-target">
            <div className="transfer2-form-target-label">
              {t("transferOfEmployees2.employee")}
            </div>
            <div className="transfer2-form-target-value">
              {target.surname} {target.name}
            </div>
          </div>
        )}
        <div className="transfer2-form-row">
          <label className="transfer2-form-label" htmlFor="edit-start">
            {t("transferOfEmployees2.releaseStart")}
          </label>
          <DateBox
            id="edit-start"
            value={startDate}
            type="datetime"
            onValueChanged={(e) => e.value && setStartDate(new Date(e.value))}
            width="100%"
          />
        </div>
        <div className="transfer2-form-row">
          <label className="transfer2-form-label" htmlFor="edit-end">
            {t("transferOfEmployees2.releaseEnd")}
          </label>
          <DateBox
            id="edit-end"
            value={endDate}
            type="datetime"
            disabled={indefinite}
            min={startDate}
            onValueChanged={(e) => e.value && setEndDate(new Date(e.value))}
            width="100%"
          />
        </div>
        <div className="transfer2-form-row">
          <CheckBox
            value={indefinite}
            text={t("transferOfEmployees2.indefinite")}
            onValueChanged={(e) => setIndefinite(!!e.value)}
          />
        </div>
        {error && <div className="transfer2-form-error">{error}</div>}
        <div className="transfer2-form-actions">
          <Button
            text={t("common.cancel")}
            onClick={onClose}
            stylingMode="outlined"
            width={isMobile ? "100%" : undefined}
            disabled={submitting}
          />
          <Button
            text={t("transferOfEmployees2.saveChanges")}
            type="default"
            stylingMode="contained"
            onClick={handleSubmit}
            width={isMobile ? "100%" : undefined}
            disabled={submitting}
          />
        </div>
      </div>
    </Popup>
  );
});
